/**
 * LA SUBIDA DEL VIDEO DEL ATR DESDE EL TELÉFONO (pedido del dueño 2026-09-17).
 *
 * El archivo va DIRECTO a R2 por partes de 8 MiB con URLs prefirmadas: reutiliza la cola de Record
 * (`lib/rec/uploader`: 3 en vuelo, reintento ×5 con backoff, pausa sin red, firma por lotes). Acá
 * solo se corta el `File` en partes exactas y se orquesta abrir → subir → completar.
 *
 * Antes de subir se OLFATEA EL CÓDEC leyendo la cabecera y la cola del archivo (el `stsd` de un
 * MOV/MP4 lleva el fourcc del video: `hvc1`/`hev1` = HEVC, `avc1`/`avc3` = H.264). Un iPhone en
 * "High Efficiency" graba HEVC, que Chrome/Edge en Windows muchas veces no reproduce: el inspector
 * ve el aviso al elegir el archivo y la oficina lo ve en el cuadro ATR. No se bloquea nada.
 */
import { api, ApiError } from './api';
import { Uploader, estimateRemainingSec, type UploaderState } from './rec/uploader';
import type { PartUrl } from './rec/types';
import type { AtrVideoDTO } from './atrVideos';

export type CodecSniff = 'hevc' | 'h264' | 'unknown';

const NEEDLES: ReadonlyArray<readonly [string, CodecSniff]> = [['hvc1', 'hevc'], ['hev1', 'hevc'], ['avc1', 'h264'], ['avc3', 'h264']];
const HEAD_BYTES = 2 * 1024 * 1024;
const TAIL_BYTES = 8 * 1024 * 1024;

function indexOfAscii(bytes: Uint8Array, needle: string): number {
  const n = needle.length;
  const c0 = needle.charCodeAt(0);
  outer: for (let i = 0; i + n <= bytes.length; i += 1) {
    if (bytes[i] !== c0) continue;
    for (let j = 1; j < n; j += 1) if (bytes[i + j] !== needle.charCodeAt(j)) continue outer;
    return i;
  }
  return -1;
}

/** El fourcc que aparece PRIMERO decide (en un archivo real solo hay uno de los dos). Puro, testeable. */
export function sniffCodecFromBytes(bytes: Uint8Array): CodecSniff {
  let best: { idx: number; codec: CodecSniff } | null = null;
  for (const [needle, codec] of NEEDLES) {
    const idx = indexOfAscii(bytes, needle);
    if (idx >= 0 && (!best || idx < best.idx)) best = { idx, codec };
  }
  return best?.codec ?? 'unknown';
}

/** Lee 2 MiB del principio y 8 MiB del final (el `moov` de un iPhone suele ir al final). Nunca el archivo entero. */
export async function sniffVideoCodec(file: Blob): Promise<CodecSniff> {
  const leer = async (a: number, b: number) => new Uint8Array(await file.slice(a, b).arrayBuffer());
  const head = await leer(0, Math.min(file.size, HEAD_BYTES));
  const h = sniffCodecFromBytes(head);
  if (h !== 'unknown' || file.size <= HEAD_BYTES) return h;
  const tail = await leer(Math.max(HEAD_BYTES, file.size - TAIL_BYTES), file.size);
  return sniffCodecFromBytes(tail);
}

export type VideoMeta = { durationMs: number | null; width: number | null; height: number | null };

/** Duración y tamaño del cuadro con un <video> sobre el blob (best-effort: si el navegador no decodifica ese códec, null). */
export function readVideoMeta(file: Blob, timeoutMs = 8_000): Promise<VideoMeta | null> {
  return new Promise((resolve) => {
    if (typeof document === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) { resolve(null); return; }
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata'; v.muted = true; v.setAttribute('playsinline', '');
    let listo = false;
    const done = (r: VideoMeta | null) => {
      if (listo) return; listo = true;
      clearTimeout(t); URL.revokeObjectURL(url); v.removeAttribute('src'); try { v.load(); } catch { /* */ }
      resolve(r);
    };
    const t = setTimeout(() => done(null), timeoutMs);
    v.onloadedmetadata = () => done({
      durationMs: Number.isFinite(v.duration) && v.duration > 0 ? Math.round(v.duration * 1000) : null,
      width: v.videoWidth || null, height: v.videoHeight || null,
    });
    v.onerror = () => done(null);
    v.src = url;
  });
}

/** Cuántas partes salen de un archivo (la última puede ser menor). */
export function partsOf(size: number, partSize: number): number {
  if (size <= 0 || partSize <= 0) return 0;
  return Math.ceil(size / partSize);
}

/**
 * EL PUT DE CADA PARTE, con un timeout LARGO. Medido en prod (2026-09-18, Manuel desde el campo): 3,8 Mbps
 * de subida → una parte de 8 MiB tarda ~18 s sola, y con varias en vuelo repartiéndose ese ancho de banda
 * puede pasar de los 3 min del timeout de Record (pensado para oficina). Un timeout corto en una red lenta
 * no acelera nada: tira la parte a medio subir y la vuelve a empezar.
 */
const PUT_TIMEOUT_LARGO_MS = 15 * 60_000;
export const EN_VUELO_ATR = 4;
export function xhrPutLargo(url: string, blob: Blob, onProgress: (loaded: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.timeout = PUT_TIMEOUT_LARGO_MS;
    xhr.upload.onprogress = (e) => { if (e.lengthComputable || e.loaded) onProgress(e.loaded); };
    xhr.onload = () => { if (xhr.status >= 200 && xhr.status < 300) resolve(); else reject(new Error(`http_${xhr.status}`)); };
    xhr.onerror = () => reject(new Error('network'));
    xhr.ontimeout = () => reject(new Error('timeout'));
    xhr.onabort = () => reject(new Error('aborted'));
    // El Blob sale SIN tipo para que no viaje Content-Type (la firma de la parte no lo incluye).
    xhr.send(blob.type ? new Blob([blob]) : blob);
  });
}

export type AtrUploadPhase = 'preparing' | 'uploading' | 'finishing' | 'done' | 'failed';
export type AtrUploadProgress = {
  phase: AtrUploadPhase;
  /** 0..100 por bytes CONFIRMADOS en R2. */
  pct: number;
  bytesDone: number; bytesTotal: number; bytesPerSec: number; remainingSec: number | null;
  offline: boolean; error: string | null; codec: CodecSniff | null;
};

export function progresoDe(s: UploaderState, codec: CodecSniff | null, phase: AtrUploadPhase = 'uploading'): AtrUploadProgress {
  const done = Math.max(0, s.totalBytes - s.pendingBytes);
  return {
    phase: s.failed ? 'failed' : phase, pct: s.totalBytes ? Math.min(100, Math.floor((done / s.totalBytes) * 100)) : 0,
    bytesDone: done, bytesTotal: s.totalBytes, bytesPerSec: s.bytesPerSec,
    remainingSec: estimateRemainingSec(s.pendingBytes, s.bytesPerSec), offline: s.offline, error: s.error, codec,
  };
}

export type AtrUploadHandle = {
  promise: Promise<{ videoId: string; video: AtrVideoDTO }>;
  /** ¿Hay algo que reintentar sin volver a elegir el archivo? (cola en `failed`, o el `complete` que se rindió). */
  canRetry(): boolean;
  /** Tras `failed`: vuelve a intentar lo que quedó en cola, o repite el `complete`. */
  retry(): void;
  /** Cancela y aborta el multipart en el backend. */
  cancel(): Promise<void>;
};

type CreateResp = { id: string; uploadId: string; partSize: number; expiresAt: string };
const COMPLETE_RETRY_MS = [1_000, 2_000, 4_000, 8_000, 15_000];

/** `POST /complete` con reintentos ante 502/503/504 y red caída (un deploy a mitad). Un 400 (`parts_missing`) no se reintenta. */
async function completar(base: string, videoId: string, body: Record<string, unknown>, sleep: (ms: number) => Promise<void>): Promise<{ video: AtrVideoDTO }> {
  for (let intento = 0; ; intento += 1) {
    try {
      return await api.post<{ video: AtrVideoDTO }>(`${base}/${videoId}/complete`, body);
    } catch (e) {
      const st = e instanceof ApiError ? e.status : 0;
      const reintentable = st === 0 || st === 502 || st === 503 || st === 504 || st === 429;
      if (!reintentable || intento >= COMPLETE_RETRY_MS.length) throw e;
      await sleep(COMPLETE_RETRY_MS[intento]!);
    }
  }
}

export type AtrUploadDeps = {
  /** 'atr' (default) o 'info': video informativo del claim, pegado a la cita y al reporte (mig 0442). */
  kind?: 'atr' | 'info';
  /** Solo 'info': el nombre que le pone el inspector. */
  caption?: string | null;
  sniff?: (f: Blob) => Promise<CodecSniff>;
  meta?: (f: Blob) => Promise<VideoMeta | null>;
  sleep?: (ms: number) => Promise<void>;
  /**
   * Dónde se abre y a qué base van partes/complete/abort. Default: el teléfono del inspector sobre SU
   * cita. La oficina sube desde la tarjeta del claim con `{ create: /api/claims/<id>/video-upload,
   * base: /api/claim-videos }` (pedido del dueño 2026-09-29).
   */
  endpoints?: { create: string; base: string };
  /** Para tests: reemplaza el PUT real a R2. */
  put?: (url: string, blob: Blob, onProgress: (loaded: number) => void) => Promise<void>;
};

/**
 * Arranca la subida de `file` para la cita ATR `apptId`. Devuelve un handle con la promesa final,
 * `retry()` y `cancel()`. `onProgress` recibe cada cambio (fase, %, velocidad, restante, error).
 */
export function startAtrUpload(file: File, apptId: string, onProgress: (p: AtrUploadProgress) => void, deps: AtrUploadDeps = {}): AtrUploadHandle {
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((r) => { setTimeout(r, ms); }));
  let up: Uploader | null = null;
  let videoId: string | null = null;
  let cancelado = false;
  let codec: CodecSniff | null = null;
  let meta: VideoMeta | null = null;
  let partsSent: number | null = null;
  const vacio: UploaderState = { partsQueued: 0, partsDone: 0, contiguousDone: 0, pendingBytes: file.size, totalBytes: file.size, bytesPerSec: 0, offline: false, failed: false, error: null, done: false };
  const createUrl = deps.endpoints?.create ?? `/api/inspector/appointments/${apptId}/video-upload`;
  const base = deps.endpoints?.base ?? '/api/inspector/atr-videos';
  const mensaje = (e: unknown) => (e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));

  /** Todo subido: `POST /complete`. Separado para poder repetirlo desde `retry()` si se rindió (deploy a mitad). */
  async function terminar(): Promise<{ videoId: string; video: AtrVideoDTO }> {
    const st = up!.state;
    onProgress({ ...progresoDe(st, codec, 'finishing'), pct: 100 });
    try {
      const r = await completar(base, videoId!, { partsSent, durationMs: meta?.durationMs ?? null, width: meta?.width ?? null, height: meta?.height ?? null, codec }, sleep);
      onProgress({ ...progresoDe(st, codec, 'done'), pct: 100 });
      return { videoId: videoId!, video: r.video };
    } catch (e) {
      onProgress({ ...progresoDe(st, codec, 'failed'), pct: 100, error: mensaje(e) });
      throw e;
    }
  }

  const promise = (async () => {
    onProgress(progresoDe(vacio, null, 'preparing'));
    codec = await (deps.sniff ?? sniffVideoCodec)(file).catch(() => 'unknown' as CodecSniff);
    onProgress(progresoDe(vacio, codec, 'preparing'));
    meta = await (deps.meta ?? readVideoMeta)(file).catch(() => null);
    if (cancelado) throw new Error('cancelled');
    const created = await api.post<CreateResp>(createUrl, {
      filename: file.name || 'video.mov', mime: file.type || null, size: file.size, codec,
      kind: deps.kind ?? 'atr', caption: deps.caption ?? null,
    });
    videoId = created.id;
    if (cancelado) { await api.post(`${base}/${videoId}/abort`, {}).catch(() => {}); throw new Error('cancelled'); }
    const partSize = created.partSize;
    // 4 partes en vuelo (más flujos TCP aprovechan mejor una red celular con latencia) y timeout largo por parte.
    up = new Uploader({
      partSize, inFlight: EN_VUELO_ATR,
      presign: (from, count) => api.post<{ urls: PartUrl[] }>(`${base}/${videoId}/parts`, { from, count }).then((r) => r.urls),
      onState: (s) => onProgress(progresoDe(s, codec, 'uploading')),
      put: deps.put ?? xhrPutLargo,
    });
    // Partes EXACTAS de `partSize` (la última, lo que quede): el cortador de Record las saca de a una.
    const n = partsOf(file.size, partSize);
    for (let i = 0; i < n; i += 1) up.push(file.slice(i * partSize, Math.min(file.size, (i + 1) * partSize)));
    partsSent = await up.finish();
    return terminar();
  })();

  return {
    promise,
    canRetry: () => Boolean(up?.state.failed) || (partsSent != null && videoId != null),
    retry: () => {
      if (up?.state.failed) up.retry();
      else if (partsSent != null && videoId) void terminar().catch(() => { /* ya se avisó por onProgress */ });
    },
    cancel: async () => {
      cancelado = true;
      up?.abort();
      if (videoId) await api.post(`${base}/${videoId}/abort`, {}).catch(() => {});
    },
  };
}
